<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getListingLink,
  resolveListingAddress,
  resolveListingId,
  resolveOfferPaymentKind,
  resolveOfferPaymentLabel,
  resolveOfferPrice,
} from 'dashboard/helper/lais/offerPresenter';
import { formatCurrency } from 'dashboard/routes/dashboard/laisLeads/utils/crmPageUtils';

const props = defineProps({
  offer: { type: Object, default: null },
  accountId: { type: [Number, String], default: null },
});

const { t } = useI18n();

const price = computed(() => resolveOfferPrice(props.offer || {}));
const paymentKind = computed(() => resolveOfferPaymentKind(props.offer || {}));
const paymentLabel = computed(() =>
  resolveOfferPaymentLabel(props.offer || {})
);
const listingId = computed(() => resolveListingId(props.offer || {}));
const listingAddress = computed(() => resolveListingAddress(props.offer || {}));
const listingLink = computed(() =>
  getListingLink(props.offer || {}, props.accountId)
);
</script>

<template>
  <div class="offer-overview-container min-w-0 max-w-full">
    <dl class="offer-overview grid min-w-0 max-w-full grid-cols-1">
      <div
        class="offer-overview__item offer-overview__price min-w-0 border-b border-lais-border-soft py-3 first:pt-0"
      >
        <dt
          class="flex items-center gap-2 text-lais-label-xs uppercase text-lais-text-muted"
        >
          <span class="i-lucide-circle-dollar-sign size-4" aria-hidden="true" />
          {{ t('PROPERTIES.OFFERS.OVERVIEW.PRICE') }}
        </dt>
        <dd class="mt-1 font-semibold text-lais-text">
          {{ formatCurrency(price) || '-' }}
        </dd>
      </div>

      <div
        v-if="paymentKind"
        class="offer-overview__item offer-overview__payment min-w-0 border-b border-lais-border-soft py-3"
      >
        <dt
          class="flex items-center gap-2 text-lais-label-xs uppercase text-lais-text-muted"
        >
          <span class="i-lucide-badge-check size-4" aria-hidden="true" />
          {{ t('PROPERTIES.OFFERS.OVERVIEW.PAYMENT') }}
        </dt>
        <dd class="mt-1 font-semibold text-lais-text">
          {{ paymentLabel }}
        </dd>
      </div>

      <div
        class="offer-overview__item offer-overview__property min-w-0 py-3 last:pb-0"
        :class="
          paymentKind
            ? 'offer-overview__property--with-payment'
            : 'offer-overview__property--without-payment'
        "
      >
        <dt
          class="flex min-w-0 items-center gap-2 text-lais-label-xs uppercase text-lais-text-muted"
        >
          <span class="i-lucide-building-2 size-4" aria-hidden="true" />
          {{
            t('PROPERTIES.OFFERS.OVERVIEW.PROPERTY', {
              id: listingId || '-',
            })
          }}
        </dt>
        <dd
          class="offer-overview__property-address mt-1 block w-full min-w-0 max-w-full whitespace-normal break-words text-lais-body-sm font-semibold text-lais-text"
        >
          {{ listingAddress || '-' }}
        </dd>
        <a
          v-if="listingLink"
          :href="listingLink"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-2 inline-flex min-w-0 items-center gap-1 break-words text-lais-label-sm font-semibold text-lais-primary"
        >
          {{ t('PROPERTIES.OFFERS.OVERVIEW.OPEN_LISTING') }}
          <span class="i-lucide-external-link size-3.5" aria-hidden="true" />
        </a>
      </div>
    </dl>
  </div>
</template>

<style scoped>
.offer-overview-container {
  container-type: inline-size;
}

.offer-overview__property-address {
  overflow-wrap: anywhere;
}

@container (min-width: 32rem) {
  .offer-overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .offer-overview__item {
    padding-block: 0;
  }

  .offer-overview__price {
    border-bottom-width: 0;
    border-right-width: 1px;
    padding-right: 1rem;
  }

  .offer-overview__payment {
    border-bottom-width: 0;
    padding-left: 1rem;
  }

  .offer-overview__property--with-payment {
    grid-column: 1 / -1;
    margin-top: 1rem;
    border-top-width: 1px;
    padding-top: 1rem;
  }

  .offer-overview__property--without-payment {
    border-left-width: 1px;
    padding-left: 1rem;
  }
}

@container (min-width: 52rem) {
  .offer-overview {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 1.45fr);
  }

  .offer-overview__price {
    padding-right: 1.25rem;
  }

  .offer-overview__payment {
    border-right-width: 1px;
    padding-inline: 1.25rem;
  }

  .offer-overview__property--with-payment {
    grid-column: auto;
    margin-top: 0;
    border-top-width: 0;
    border-left-width: 0;
    padding-top: 0;
    padding-left: 1.25rem;
  }

  .offer-overview__property--without-payment {
    grid-column: span 2 / span 2;
    padding-left: 1.25rem;
  }
}
</style>
